import React, { useMemo, useRef, useState } from 'react';
import { colors, fonts, frames, glyphs, icons, useMediaQuery } from '../../../Styles';
import PageHeader from '../../../Components/BaseComponents/pageHeader';
import { useTranslation } from 'react-i18next';
import { SystemData } from '../../../Assets/TestData/systems';
import Setting from '../../../Components/Settings/setting';
import { ButtonList, InputField, SelectField } from '../../../Components';
import { ButtonTypes, FieldStyles, FieldTypes, ListData_ValueTypes, TableStyles_Types } from '../../../Definitions';
import { AICO_IO_Data } from '../../../Assets/TestData/controllers';
import { useEditableColumnConstructor } from '../../../Components/Tables/_Tables/useEditableColumnConstructor';
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { ReactiveTable } from '../../../Components/Tables/reactiveTableRenderer';
import { InputField_Handles } from '../../../Components/BaseComponents/input';

export default function FreedaLocationFeeder() {
  const { t } = useTranslation();
  const mobileXs = useMediaQuery(`(max-width: 570px)`);

  // System Name
  const { systemName } = useMemo(() => {
    const systemData = SystemData.find((system) => system.systemId === 2);
    return {
      systemName: systemData?.systemName || "",
    };
  }, []);

  // Find label in select field options corresponding to their respective ID
  const findLabelById = (id: number, options: { label: string, id: number }[]) => {
    const option = options.find(option => option.id === id);
    return option ? option.label : "";
  };

  // #region | Daily Rotation
  const [dailyRotation, setDailyRotation] = useState<number>(0)

  const dailyRotationOptions = [    
    { label: t("settingsPages.locationFeederSettings.on"), id: 0 },
    { label: t("settingsPages.locationFeederSettings.off"), id: 1 },
  ]
  // #endregion

  // #region | Feed Balance
  const [unit, setUnit] = useState<number>(0)

  const unitOptions = [    
    { label: t("units.kg"), id: 0 },
    { label: t("units.percentage"), id: 1 },
  ]
  // #endregion
  
  // #region | Board Types
  const [boardType, setBoardType] = useState<number>(0)

  const boardTypeOptions = [    
    { label: "Board Type 1", id: 0 },
    { label: "Board Type 2", id: 1 },
    { label: "Board Type 3", id: 2 },
    { label: "Board Type 4", id: 3 }
  ]
  // #endregion

  // #region | Systems
  const selectedSystem = SystemData.find(e => e.systemId === 2) || { name: "No data", systemId: 2 };
  const defaultSystem = systemName

  // #endregion
  
  // #region | Alert when boardname already excists
  const inputRef=useRef<InputField_Handles>(null)
  const handleCheckBoardName = () => {
    if (inputRef.current) {
      const name: string = (inputRef.current?.context as string).toUpperCase();
      const nameExists = AICO_IO_Data.some(board => board.name.toUpperCase() === name);
  
      if (nameExists) {
        alert('Board name already exists!');
      } else {
        console.log(name); // TODO: Save board to db
      }
    }
  };
  // #endregion
  
  // #region | Boards Table
  const boardData = useMemo(() => (
    AICO_IO_Data
      .filter(board => {
        const system = SystemData.find(sys => sys.systemId === board.systemId);
        return system && (system.systemId === 2) || board.systemId === undefined;
      })
      .map((board) => {
        const system = SystemData.find(sys => sys.systemId === board.systemId);
        const systemName = system && (system.systemId === 2) 
        ? system.systemName 
        : "No System Assigned";
        return {
          id: board.id,
          name: board.name,
          type: board.type,
          system: systemName
        };
      })
  ), []);

  const accessorData = useMemo(() => ([
    {headerValue: "Id", accessor: "id", type: ListData_ValueTypes.Number, width: "120px"},
    {headerValue: "Name", accessor: "name", type: ListData_ValueTypes.String},
    {headerValue: "Type", accessor: "type", type: ListData_ValueTypes.Number},
    {headerValue: "System", accessor: "system", type: ListData_ValueTypes.String},
  ]),[])
  
  const columns = useEditableColumnConstructor({accessorData, tableStyle: TableStyles_Types.newSecondaryTable, canEdit: false });
  
  const table = useReactTable({
    columns,
    data: boardData,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel()
  })
  
  const reactiveTable = useMemo(() => {if (boardData.length!==0) {return ReactiveTable({table, tableType: TableStyles_Types.newSecondaryTable, emptyText: "Empty text", renderFooter: false})}}, [table, boardData.length])
  // #endregion

  return (
    <frames.Page_inner>
      <frames._page_inner_header>
        <PageHeader topHeader={systemName} subHeader={t("settings")}/>
      </frames._page_inner_header>
      <frames._page_inner_content_scroll>
        <div style={{display: "flex", gap: "20px", alignItems: "center", justifyContent: "center", flexDirection: "column"}}>
          <Setting header={t("settingsPages.locationFeederSettings.dailyRotation")} startEnhancer={glyphs.Setting({size: "25px"})} content={
            <div style={{display: "flex", flexDirection: "column", alignItems: mobileXs ? "center" : "start"}}>
              <SelectField 
                overrides={{
                  fieldStyle: FieldStyles.greyTertiary, 
                  fieldType: FieldTypes.primaryInputField, 
                  textStart: true
                }}
                defaultValue={findLabelById(dailyRotation, dailyRotationOptions)}
                options={dailyRotationOptions}
                onChange={(e) => setDailyRotation(dailyRotation)}
              />
              <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, marginTop: "5px", textAlign: mobileXs ? "center" : "left"}}>{t("settingsPages.locationFeederSettings.dailyRotationDisclaimer")}</p>
            </div>
          }/>

          <Setting header={t("settingsPages.locationFeederSettings.feedInSilos")} startEnhancer={glyphs.Setting({size: "25px"})} content={
            <>
            </>
          }/>

          <Setting header={t("animalPages.animal.feedBalance")} startEnhancer={glyphs.Condition({size: "25px"})} content={
            <div style={{display: "flex", flexWrap: "wrap", rowGap: "10px", columnGap: "5px", width: "100%", justifyContent: mobileXs ? "center" : "start"}}>
              <div style={{display: "flex", flexDirection: "column"}}>
                <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyDarkPrimary, padding: "0px 0px 2px 22px" }}>{t("settingsPages.globalSettings.units")}</p>
                <SelectField 
                  overrides={{
                    fieldStyle: FieldStyles.greyTertiary, 
                    fieldType: FieldTypes.primaryInputField, 
                    textStart: true
                  }}
                  defaultValue={findLabelById(unit, unitOptions)}
                  options={unitOptions}
                  onChange={([selectedOption]) => selectedOption && setUnit(selectedOption.id as number)}
                  />
              </div>
              <div style={{display: "flex", flexDirection: "column"}}>
                <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyDarkPrimary, padding: "0px 0px 2px 22px" }}>{ t("settingsPages.locationFeederSettings.attentionLimit")+(unit === 0 ? ` (${t("units.kg")})` : ` (${t("units.pct")})`)}</p>
                <InputField 
                  fieldType={FieldTypes.primaryInputField} 
                  fieldStyle={FieldStyles.greyTertiary} 
                  textStart={true} 
                  placeholder={unit === 0 ? t("units.kg")+"..." : t("units.pct")+"..."}
                />
              </div>
            </div>
          }/>

          <Setting header={t("settingsPages.locationFeederSettings.inputOutputConnections")} startEnhancer={glyphs.Setting({size: "25px"})} content={
            <div>
              
            </div>
          }/>

          <Setting header={t("settingsPages.locationFeederSettings.feedSpeed")} startEnhancer={glyphs.Setting({size: "25px"})} content={
           <div style={{display: "flex", flexWrap: "wrap", rowGap: "10px", columnGap: "5px", width: "100%", justifyContent: mobileXs ? "center" : "start"}}>
           <div style={{display: "flex", flexDirection: "column"}}>
             <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyDarkPrimary, padding: "0px 0px 2px 22px" }}>{t("settingsPages.locationFeederSettings.minimumFeedSpeed")}</p>
             <InputField 
               fieldType={FieldTypes.primaryInputField} 
               fieldStyle={FieldStyles.greyTertiary} 
               textStart={true} 
               placeholder={t("units.g")+"/"+t("units.min")+"..."}
             />
           </div>
           <div style={{display: "flex", flexDirection: "column"}}>
             <p style={{ ...fonts.header, ...fonts.fontSize4, color: colors.greyDarkPrimary, padding: "0px 0px 2px 22px" }}>{t("settingsPages.locationFeederSettings.maximumFeedSpeed")}</p>
             <InputField 
               fieldType={FieldTypes.primaryInputField} 
               fieldStyle={FieldStyles.greyTertiary} 
               textStart={true} 
               placeholder={t("units.g")+"/"+t("units.min")+"..."}
             />
           </div>
         </div>
          }/>

          {/* I/O Boards */}
          <Setting startEnhancer={glyphs.User({size: "25px"})} overrides={{content: {padding: "0px", height: "100%", maxHeight: "300px", overflow: "hidden", borderBottomRightRadius: "5px"}}} header={"I/O Boards"} content={
            <>
              <div style={{padding: mobileXs ? "10px 10px 0px 10px" : "10px 20px 0px 20px"}}>
              <div style={{display: "flex", flexWrap: "wrap", gap: "5px", maxWidth: mobileXs ? "280px" : "auto", margin: mobileXs ? "0 auto" : "none"}}>
                  <InputField ref={inputRef}
                    placeholder={"Board Name..."} 
                    fieldStyle={FieldStyles.greyTertiary} 
                    fieldType={FieldTypes.primaryInputField} 
                    textStart={true} 
                  />
                  <SelectField 
                    overrides={{
                      fieldStyle: FieldStyles.greyTertiary, 
                      fieldType: FieldTypes.primaryInputField, 
                      textStart: true,
                      fieldWidth: "137.5px"
                    }}
                    defaultValue={findLabelById(boardType, boardTypeOptions)}
                    options={boardTypeOptions}
                    onChange={(e) => setBoardType(boardType)}
                  />
                  <InputField disabled={true} context={defaultSystem} placeholder={"Board Name..."} fieldStyle={FieldStyles.greyTertiary} fieldType={FieldTypes.shortInputField} textStart={true} />
                  <div style={{display: "flex", flexDirection: "row", gap: "5px"}}>
                    <ButtonList
                      contents={[t("buttons.add")]}
                      onClick={handleCheckBoardName}
                      buttonType={ButtonTypes.textButton}
                      overrides={{
                        baseButton: {backgroundColor: colors.greyLightPrimary, height: "30px", color: colors.greyDarkPrimary, ...fonts.fontSize4, padding: "0 10px"}
                      }}
                    />
                  </div>
                </div>
                <div style={{height: "2px", backgroundColor: colors.greyLightPrimary, width: "100%", margin: "15px 0px 0px 0px"}}/>
              </div>
              {reactiveTable}
            </>
          }/>

        </div>
      </frames._page_inner_content_scroll>
    </frames.Page_inner>
  );
}